import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { AIChatButton } from './components/AIChat';
import { AnalyticsTracker } from './components/AnalyticsTracker';

// Layout
import { Layout } from './components/layout/Layout';

// Auth Pages
import { LoginPage, RegisterPage } from './pages/AuthPages';
import VerifyInvitePage from './pages/VerifyInvitePage';

// Main Pages
import { DashboardPage } from './pages/DashboardPage';
import { AssetsListPage, AssetDetailPage, AssetFormPage } from './pages/AssetsPages';
import AssetImportPage from './pages/AssetImportPage';
import ExternalAllocateRedirect from './pages/ExternalAllocateRedirect';
import { QRScannerPage } from './pages/QRScannerPage';
import { ScanRedirectPage } from './pages/ScanRedirectPage';
import { InspectionsListPage, InspectionDetailPage, PerformInspectionPage } from './pages/InspectionsPages';
import { DefectsListPage, DefectDetailPage, DefectFormPage } from './pages/DefectsPages';
import { WorkOrdersListPage, WorkOrderDetailPage, WorkOrderFormPage } from './pages/WorkOrdersPages';
import { MaintenanceListPage } from './pages/MaintenancePages';
import { TemplatesListPage } from './pages/TemplatesPages';
import { UsersListPage } from './pages/UsersPages';
import { DocumentsPage } from './pages/DocumentsPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { SettingsPage } from './pages/SettingsPage';
import UserProfilePage from './pages/UserProfilePage';
import { getLandingPath } from './utils/navPrefs';import BillingPage from './pages/BillingPage';
import { HelpCentrePage, HelpArticlePage, AIHelpPage } from './pages/HelpPages';
import ContactSupportModal from './components/ContactSupportModal';
import { ComplianceStandardsPage } from './pages/ComplianceStandardsPage';
import { TaskLibraryPage } from './pages/TaskLibraryPage';
import { CertificateRegisterPage } from './pages/CertificateRegisterPage';
import { InsightsPage } from './pages/InsightsPage';
import { PricingPage } from './pages/PricingPage';
import { DataManagementPage } from './pages/DataManagementPage';
import { ReportsPage } from './pages/ReportsPage';
import { WorkshopPage } from './pages/WorkshopPage';
import FleetMapPage from './pages/FleetMapPage';
import InventoryPage from './pages/InventoryPage';

// Mobile Workshop Mode
import MobileApp from './pages/mobile/MobileApp';

// Public Asset Viewer (no auth)
import PublicAssetPage from './pages/PublicAssetPage';
import LandingPage from './pages/LandingPage';
import TestDrivePage from './pages/TestDrivePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import OnboardingPage from './pages/OnboardingPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Super Admin Portal (separate from tenant auth)
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminTenantsPage from './pages/admin/AdminTenantsPage';
import AdminTelematicsPage from './pages/admin/AdminTelematicsPage';
import AdminVdaqHealthPage from './pages/admin/AdminVdaqHealthPage';
import AdminBillingPage from './pages/admin/AdminBillingPage';
import AdminAuditLogPage from './pages/admin/AdminAuditLogPage';
import AdminBroadcastPage from './pages/admin/AdminBroadcastPage';
import AdminLifecyclePage from './pages/admin/AdminLifecyclePage';
import AdminAiChatLogsPage from './pages/admin/AdminAiChatLogsPage';
import AdminAiKeysPage from './pages/admin/AdminAiKeysPage';
import { AdminProtectedRoute } from './pages/admin/AdminProtectedRoute';
import { IdleTimeout } from './components/IdleTimeout';

// Protected Route Component with AI Chat
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, isMechanic } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Mechanics are field users — all their work happens in the mobile app.
  // Bounce them to /mobile if they land on a desktop route.
  if (isMechanic()) {
    return <Navigate to="/mobile" replace />;
  }
  
  return (
    <Layout>
      {children}
      <AIChatButton />
      <ContactSupportModal />
      <IdleTimeout />
    </Layout>
  );
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to={getLandingPath()} replace />;
  }
  
  return children;
};

// Preserves Stripe redirect query params (?checkout=success&session_id=…)
// when forwarding the legacy /settings/billing URL to /settings?tab=billing.
const BillingRedirect = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('tab', 'billing');
  return <Navigate to={`/settings?${params.toString()}`} replace />;
};


function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/verify-invite" element={<VerifyInvitePage />} />
      
      {/* QR Scan Redirect - Semi-public (shows login if not authenticated) */}
      <Route path="/scan/:assetId" element={<ScanRedirectPage />} />
      
      {/* Workshop Command Center - Standalone auth */}
      <Route path="/workshop" element={<WorkshopPage />} />
      
      {/* Mobile Workshop Mode - Standalone auth */}
      <Route path="/mobile/*" element={<MobileApp />} />
      
      {/* Public Asset Viewer - No auth required */}
      <Route path="/public/asset/:code" element={<PublicAssetPage />} />
      
      {/* Test Drive - No auth required */}
      <Route path="/test-drive" element={<TestDrivePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />

      {/* Password reset — no auth required */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Onboarding — authenticated but rendered without the main Layout/Sidebar */}
      <Route path="/onboarding" element={<OnboardingPage />} />

      {/* Super Admin Portal — separate auth (email OTP) */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<AdminProtectedRoute><AdminTenantsPage /></AdminProtectedRoute>} />
      <Route path="/admin/tenants" element={<AdminProtectedRoute><AdminTenantsPage /></AdminProtectedRoute>} />
      <Route path="/admin/telematics" element={<AdminProtectedRoute><AdminTelematicsPage /></AdminProtectedRoute>} />
      <Route path="/admin/vdaq-health" element={<AdminProtectedRoute><AdminVdaqHealthPage /></AdminProtectedRoute>} />
      <Route path="/admin/billing" element={<AdminProtectedRoute><AdminBillingPage /></AdminProtectedRoute>} />
      <Route path="/admin/audit" element={<AdminProtectedRoute><AdminAuditLogPage /></AdminProtectedRoute>} />
      <Route path="/admin/broadcast" element={<AdminProtectedRoute><AdminBroadcastPage /></AdminProtectedRoute>} />
      <Route path="/admin/lifecycle" element={<AdminProtectedRoute><AdminLifecyclePage /></AdminProtectedRoute>} />
      <Route path="/admin/ai-logs" element={<AdminProtectedRoute><AdminAiChatLogsPage /></AdminProtectedRoute>} />
      <Route path="/admin/ai-keys" element={<AdminProtectedRoute><AdminAiKeysPage /></AdminProtectedRoute>} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      
      {/* Assets */}
      <Route path="/assets" element={<ProtectedRoute><AssetsListPage /></ProtectedRoute>} />
      <Route path="/assets/import" element={<ProtectedRoute><AssetImportPage /></ProtectedRoute>} />
      <Route path="/assets/new" element={<ProtectedRoute><AssetFormPage /></ProtectedRoute>} />
      <Route path="/assets/:id" element={<ProtectedRoute><AssetDetailPage /></ProtectedRoute>} />
      <Route path="/assets/:id/edit" element={<ProtectedRoute><AssetFormPage /></ProtectedRoute>} />

      {/* Allocate deep-link contract: opened from inside EQUIPR Allocate */}
      {/* Two-part (recommended): /external/allocate/{clientId}/{allocateId} */}
      <Route path="/external/allocate/:clientId/:allocateId" element={<ProtectedRoute><ExternalAllocateRedirect /></ProtectedRoute>} />
      {/* Legacy single-id: /external/allocate/{allocateId} — kept for backwards compat */}
      <Route path="/external/allocate/:allocateId" element={<ProtectedRoute><ExternalAllocateRedirect /></ProtectedRoute>} />
      
      {/* QR Scanner */}
      <Route path="/scanner" element={<ProtectedRoute><QRScannerPage /></ProtectedRoute>} />
      
      {/* Inspections */}
      <Route path="/inspections" element={<ProtectedRoute><InspectionsListPage /></ProtectedRoute>} />
      <Route path="/inspections/new" element={<ProtectedRoute><PerformInspectionPage /></ProtectedRoute>} />
      <Route path="/inspections/:id" element={<ProtectedRoute><InspectionDetailPage /></ProtectedRoute>} />
      
      {/* Defects */}
      <Route path="/defects" element={<ProtectedRoute><DefectsListPage /></ProtectedRoute>} />
      <Route path="/defects/new" element={<ProtectedRoute><DefectFormPage /></ProtectedRoute>} />
      <Route path="/defects/:id" element={<ProtectedRoute><DefectDetailPage /></ProtectedRoute>} />
      
      {/* Work Orders */}
      <Route path="/work-orders" element={<ProtectedRoute><WorkOrdersListPage /></ProtectedRoute>} />
      <Route path="/work-orders/new" element={<ProtectedRoute><WorkOrderFormPage /></ProtectedRoute>} />
      <Route path="/work-orders/:id" element={<ProtectedRoute><WorkOrderDetailPage /></ProtectedRoute>} />
      
      {/* Maintenance */}
      <Route path="/maintenance" element={<ProtectedRoute><MaintenanceListPage /></ProtectedRoute>} />

      {/* Fleet Map — full-fleet Leaflet view with clustering */}
      <Route path="/fleet-map" element={<ProtectedRoute><FleetMapPage /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
      
      {/* Reports */}
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><TemplatesListPage /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><UsersListPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/data-management" element={<ProtectedRoute><DataManagementPage /></ProtectedRoute>} />
      <Route path="/compliance" element={<ProtectedRoute><ComplianceStandardsPage /></ProtectedRoute>} />
      <Route path="/certificates" element={<ProtectedRoute><CertificateRegisterPage /></ProtectedRoute>} />
      <Route path="/insights" element={<ProtectedRoute><InsightsPage /></ProtectedRoute>} />
      <Route path="/pricing" element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
      <Route path="/task-library" element={<ProtectedRoute><TaskLibraryPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      {/* Legacy /settings/billing — redirect to the new Billing tab inside Settings.
          Stripe success/cancel callbacks land here with ?checkout=... query params; we
          preserve those by appending the existing search string after the redirect. */}
      <Route path="/settings/billing" element={<ProtectedRoute><BillingRedirect /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><HelpCentrePage /></ProtectedRoute>} />
      <Route path="/help/ai" element={<ProtectedRoute><AIHelpPage /></ProtectedRoute>} />
      <Route path="/help/:slug" element={<ProtectedRoute><HelpArticlePage /></ProtectedRoute>} />
      
      {/* Redirects */}
      <Route path="/" element={<LandingPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SettingsProvider>
          <ThemeProvider>
            <AnalyticsTracker />
            <AppRoutes />
            <Toaster position="top-right" richColors offset={80} />
          </ThemeProvider>
        </SettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
